import { useInertiaCable } from "@inertia-cable/react";
import { Head, router, usePage } from "@inertiajs/react";
import { t } from "@travily/i18n";
import { messagesPath } from "@travily/routes";
import type MessagesShow from "@travily/types/pages/MessagesShow";
import { Frame } from "@ui/components/Frame";
import { TopBarBorder } from "@ui/components/TopBar/TopBarBorder";
import { TypingIndicator } from "@ui/components/TypingIndicator";
import { useCallback, useMemo } from "react";
import { useConversationSend } from "@/hooks/use-conversation-send";
import { useMessageReactions } from "@/hooks/use-message-reactions";
import { useNavigateBack } from "@/hooks/use-navigate-back";
import { useTypingIndicator } from "@/hooks/use-typing-indicator";
import { PageFrame } from "@/layouts/page-frame";
import type { SharedProps } from "@/types";
import { ConversationTopBar } from "./components/ConversationDetailView";
import { ConversationListPanel } from "./conversation-list-panel";
import { MessagesShowView } from "./show.view";

export default function MessagesShowPage({
  participations,
  conversation,
  counterpart_read_at,
  messages,
  other_account,
  reactions: serverReactions,
  cable_stream,
  typing_stream,
}: MessagesShow) {
  const { url, props } = usePage<SharedProps>();
  const { current_user } = props;
  const onBack = useNavigateBack(messagesPath());
  // Enquiry links pass a draft via the /messages?to= redirect; the user still has to press send.
  const draft = new URLSearchParams(url.split("?")[1] ?? "").get("draft") ?? "";
  // Once sent, the enquiry has to leave the address bar, or reopening this URL seeds the composer
  // with it again and the host receives it twice. A client visit: the URL is all that changes.
  const clearSentDraft = useCallback(() => {
    if (!draft) return;

    router.replace({ url: withoutDraft(url), preserveScroll: true, preserveState: true });
  }, [draft, url]);

  // The conversation cable only owns the open thread's `messages`. The list
  // (`participations`) + nav count are refreshed by onUnreadChanged below, so we
  // don't double-fetch participations on every inbound message.
  useInertiaCable(cable_stream, { only: ["messages", "reactions", "counterpart_read_at"] });

  const { reactions, toggle: toggleReaction } = useMessageReactions(conversation.sqid, serverReactions);
  const {
    isSending,
    messages: allMessages,
    messageValue,
    send,
    setMessageValue,
    uploads,
  } = useConversationSend({
    accountHandle: other_account.handle,
    conversationSqid: conversation.sqid,
    initialMessageValue: draft,
    messages,
    onSent: clearSentDraft,
  });
  // Whether a receipt shows is decided by the read marker alone; the name is only its copy, and a
  // counterpart with no display name still read the thread.
  const readers = useMemo(
    () => (other_account.display_name ? [other_account.display_name] : []),
    [other_account.display_name],
  );

  const { isPeerTyping, onTyping } = useTypingIndicator({
    currentUserId: current_user?.id,
    messages: allMessages,
    stream: typing_stream,
  });
  const typingIndicator = isPeerTyping ? (
    <TypingIndicator
      label={t("components.views.shared.message_list.typing", { name: other_account.display_name ?? "" })}
    />
  ) : null;

  return (
    <PageFrame
      contentFills
      contentMaxWidth="full"
      contentPadded={false}
      footerFullBleed
      hideBottomBar
      hideFooter
      hideTopBarNav
      onUnreadChanged={() => {
        // Reading in place: when this tab is focused, tell the server to mark the
        // conversation read (X-Conversation-Focused) so the nav unread clears
        // without a re-navigation. A backgrounded tab still refreshes the list +
        // count but stays unread. This reload is the sole owner of
        // unread_messages_count here (see use-notification-channel), so there is
        // no race with the generic nav refresh.
        const focused = typeof document !== "undefined" && document.visibilityState === "visible";
        router.reload({
          only: ["participations", "unread_messages_count"],
          headers: focused ? { "X-Conversation-Focused": "true" } : {},
        });
      }}
      topBarBottomContent={<TopBarBorder />}
      mobileTopBar={<ConversationTopBar account={other_account} onBack={onBack} />}
    >
      <Head title={t("web.messages.index.meta_title")} />
      <Frame.LeftPane collapse="drill" maxWidth="medium">
        <ConversationListPanel activeConversationSqid={conversation.sqid} participations={participations} url={url} />
      </Frame.LeftPane>
      <MessagesShowView
        account={other_account}
        currentUserId={current_user?.id}
        isSending={isSending}
        messageValue={messageValue}
        messages={allMessages}
        onMessageChange={setMessageValue}
        attachments={uploads.files}
        onAddFiles={uploads.addFiles}
        onRemoveFile={uploads.removeFile}
        onSendMessage={send}
        onToggleReaction={toggleReaction}
        onTyping={onTyping}
        reactions={reactions}
        readAt={counterpart_read_at}
        readers={readers}
        typingIndicator={typingIndicator}
      />
    </PageFrame>
  );
}

function withoutDraft(url: string): string {
  const [path, query] = url.split("?");
  const params = new URLSearchParams(query ?? "");
  params.delete("draft");
  const rest = params.toString();
  return rest ? `${path}?${rest}` : (path ?? url);
}
